import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { APP_LAUNCHER_LABELS } from './AppLauncher.constants'
import styles from './AppLauncher.module.css'

// React 16 has no useId. Allocate once per mounted instance, never per render.
let nextLauncherId = 0

const focusable = element => Array.from(element.querySelectorAll(
  'button:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
))

const Icon = ({ kind = 'apps' }) => (
  <svg width={kind === 'apps' ? 32 : 20} height={kind === 'apps' ? 32 : 20}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    {kind === 'close' ? <path d="m6 6 12 12M6 18 18 6" /> :
      [[3, 3], [14, 3], [14, 14], [3, 14]].map(([x, y]) => (
        <rect key={`${x}:${y}`} x={x} y={y} width={7} height={7} rx={1} />
      ))}
  </svg>
)

/** Presentational browser dialog. See README.md for the host-owned contract. */
const AppLauncher = ({
  isOpen, theme, groups, status, catalogError, launchError, isLaunchPending,
  pendingItemId, returnFocusRef, onLaunch, onClose, onRefresh, onRetryLaunch,
  onViewAll, isViewAllPending = false, labels,
}) => {
  const text = { ...APP_LAUNCHER_LABELS, ...labels }
  const viewAllPending = Boolean(onViewAll && isViewAllPending)
  const busy = isLaunchPending || viewAllPending
  const viewAllDisabled = status === 'idle' || status === 'loading' || busy
  const idRef = useRef(null)
  if (idRef.current === null) {
    nextLauncherId += 1
    idRef.current = `promo-app-launcher-${nextLauncherId}`
  }
  const id = idRef.current
  const dialogRef = useRef(null)
  const closeRef = useRef(null)
  const [dialogPosition, setDialogPosition] = useState({ anchored: false, left: 12, bottom: 12 })
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const selectedGroup = groups.find(group => group.id === selectedGroupId) || groups[0]
  const hasMultipleGroups = groups.length > 1
  const focusRef = useRef(returnFocusRef)

  useLayoutEffect(() => { focusRef.current = returnFocusRef })
  useEffect(() => { setSelectedGroupId(groups.length ? groups[0].id : '') }, [groups])

  useLayoutEffect(() => {
    if (!isOpen) return
    const updatePosition = () => {
      const anchor = focusRef.current && focusRef.current.current
      const rect = anchor && anchor.getBoundingClientRect()
      setDialogPosition({
        anchored: window.innerWidth > 924,
        left: rect ? Math.max(12, Math.min(rect.left, window.innerWidth - 332)) : 12,
        bottom: rect ? Math.max(12, window.innerHeight - rect.top + 8) : 12,
      })
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) return
    const opener = focusRef.current ? focusRef.current.current : document.activeElement
    if (closeRef.current) closeRef.current.focus()
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected &&
        !opener.matches(':disabled, [aria-disabled="true"], [inert], [inert] *')) {
        opener.focus()
      }
    }
  }, [isOpen])

  // Repair focus when a catalog update removes or disables the active control.
  useLayoutEffect(() => {
    if (isOpen && dialogRef.current &&
      (!dialogRef.current.contains(document.activeElement) ||
        (document.activeElement && document.activeElement.matches(':disabled, [aria-disabled="true"]')))) {
      if (closeRef.current) closeRef.current.focus()
    }
  })

  if (!isOpen) return null

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onClose()
    } else if (event.key === 'Tab' && dialogRef.current) {
      const elements = focusable(dialogRef.current)
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        if (last) last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        if (first) first.focus()
      }
    }
  }

  const renderGroup = (group, groupIndex) => (
    <section key={group.id} className={styles.tenantGroup} aria-label={group.label}>
      {!hasMultipleGroups && <h3>{group.label}</h3>}
      <p className={styles.groupLabel}>{text.groupCaption}</p>
      {group.items.length === 0 ? (
        <p className={styles.groupEmpty} role="status">{text.emptyGroup}</p>
      ) : (
        <div className={styles.itemGrid}>
          {group.items.map((item, itemIndex) => {
            const reasonId = `${id}-reason-${groupIndex}-${itemIndex}`
            return (
              <div key={item.id} className={styles.item}>
                <button type="button" className={styles.appButton} aria-label={item.label}
                  title={item.label} aria-describedby={item.disabled ? reasonId : undefined}
                  disabled={item.disabled || busy}
                  onClick={event => onLaunch(item, event.currentTarget)}>
                  {item.icon ? (
                    <span className={styles.iconWrap} aria-hidden="true">
                      <img className={item.icon.dark ? styles.iconLight : styles.iconSingle}
                        src={item.icon.light} alt="" />
                      {item.icon.dark && <img className={styles.iconDark} src={item.icon.dark} alt="" />}
                    </span>
                  ) : <span className={styles.iconFallback} aria-hidden="true"><Icon /></span>}
                  <span className={styles.appLabel}>
                    {isLaunchPending && pendingItemId === item.id ? text.pending : item.label}
                  </span>
                </button>
                {item.disabled && (
                  <span id={reasonId} className={styles.srOnly}>
                    {item.disabledReason == null ? text.disabledFallback : item.disabledReason}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )

  const catalog = status === 'idle' || status === 'loading' ? (
    <p className={styles.emptyState} role="status">{text.loading}</p>
  ) : status === 'error' ? (
    <div className={`${styles.emptyState} ${styles.catalogError}`}>
      <p role="alert">{catalogError == null ? text.catalogError : catalogError}</p>
      {onRefresh && <button className={styles.retryButton} type="button"
        disabled={onViewAll ? busy : undefined} onClick={onRefresh}>{text.retry}</button>}
    </div>
  ) : (
    <>
      {launchError && (
        <div className={styles.launchError} role="alert">
          <p>{launchError}</p>
          {onRetryLaunch && <button className={styles.retryButton} type="button" disabled={busy}
            onClick={event => onRetryLaunch(event.currentTarget)}>{text.retry}</button>}
        </div>
      )}
      {isLaunchPending && <p className={styles.srOnly} role="status">{text.pending}</p>}
      {groups.length === 0 ? <p className={styles.emptyState} role="status">{text.emptyGroups}</p> : (
        <div className={styles.itemArea} aria-busy={busy}>
          {hasMultipleGroups && selectedGroup ? (
            <>
              <div className={styles.tenantSelector}>
                <select id={`${id}-tenant`} aria-label={text.tenantSelector} value={selectedGroup.id}
                  onChange={event => setSelectedGroupId(event.target.value)}>
                  {groups.map(group => <option key={group.id} value={group.id}>{group.label}</option>)}
                </select>
              </div>
              {renderGroup(selectedGroup, 0)}
            </>
          ) : groups.map(renderGroup)}
        </div>
      )}
    </>
  )

  return createPortal(
    <div className={styles.overlay} data-theme={theme} role="presentation"
      onClick={event => { if (event.target === event.currentTarget) onClose() }}>
      <div ref={dialogRef} className={styles.dialog}
        data-position={dialogPosition.anchored ? 'anchored' : 'centered'}
        style={{ '--app-launcher-left': `${dialogPosition.left}px`, '--app-launcher-bottom': `${dialogPosition.bottom}px` }}
        role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} onKeyDown={handleKeyDown}>
        <header className={styles.header}>
          <h2 id={`${id}-title`}>{text.title}</h2>
          <span className={styles.headerActions}>
            <button ref={closeRef} type="button" className={styles.closeButton}
              aria-label={text.close} onClick={onClose}><Icon kind="close" /></button>
          </span>
        </header>
        {onViewAll ? <div className={styles.catalogContent}>{catalog}</div> : catalog}
        {onViewAll && (
          <footer className={styles.footer}>
            <button type="button" className={styles.viewAllButton} disabled={viewAllDisabled}
              aria-busy={viewAllPending}
              onClick={event => { if (!viewAllDisabled) onViewAll(event.currentTarget) }}>{text.viewAll}</button>
            {viewAllPending && <span className={styles.srOnly} role="status">{text.pending}</span>}
          </footer>
        )}
      </div>
    </div>,
    document.body
  )
}

export default AppLauncher
